import { FC, useEffect, useRef } from "react";
import { CircularProgress, Skeleton, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridEventListener, GridRowSelectionModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import CustomNoRowsOverlay from "../../../../features/results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import { useCurrentTheme } from "../../../../theme/theme";
import { ThemeColors } from "../../../../theme/themeValues";
import PaperCard from "../../../widgets/PaperCard";
import PaperCardHeader from "../../../widgets/PaperCard/PaperCardHeader";
import { useCustomTableDataStyles } from "./useCustomTableDataStyles";

type Props = {
	columns: GridColDef[];
	rows: { id: string | number; [key: string]: any }[];
	isLoading: boolean;
	onReload?: VoidFunction;
	title?: string;
	elevation?: number;
	onRowClick?: GridEventListener<"rowClick">;
	isSecondaryTable?: boolean;
	rowSelectionModel?: GridRowSelectionModel;
	onRowSelectionChange?: (newSelection: GridRowSelectionModel) => void;
	onLoadMore?: VoidFunction;
	hasMore?: boolean;
	fixedHeight?: number;
	isPrimaryColor?: boolean;
};

/**
 * TableData
 *
 * Generic wrapper around MUI DataGrid.
 * - Displays table with optional infinite scroll, selection, and reload.
 * - Supports both main and secondary tables (with checkbox styling).
 * - Shows skeleton, loading spinner, or “no more data” message as needed.
 */
const TableData: FC<Props> = ({
	columns,
	rows,
	isLoading,
	onReload,
	title,
	elevation,
	onRowClick,
	isSecondaryTable,
	rowSelectionModel,
	onRowSelectionChange,
	onLoadMore,
	hasMore,
	fixedHeight,
	isPrimaryColor,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("feature.units");
	const { theme } = useCurrentTheme();
	const tableSx = useCustomTableDataStyles();

	// Attach scroll listener for infinite loading
	useEffect(() => {
		if (!onLoadMore || !hasMore) return;

		const gridScroller = containerRef.current?.querySelector(
			".MuiDataGrid-virtualScroller"
		) as HTMLDivElement | null;

		if (!gridScroller) return;

		const handleInnerScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = gridScroller;
			const isNearBottom = scrollHeight - scrollTop - clientHeight < 120;

			if (isNearBottom && !isLoading) {
				onLoadMore();
			}
		};

		gridScroller.addEventListener("scroll", handleInnerScroll);
		return () => gridScroller.removeEventListener("scroll", handleInnerScroll);
	}, [onLoadMore, hasMore, isLoading]);

	// Show skeleton when data is not ready
	if (!rows || isLoading) {
		return <Skeleton variant="rounded" height={fixedHeight || "100%"} width="100%" />;
	}

	// Styles applied only if it's a secondary table (used in accordions)
	const secondaryTableStyles = isSecondaryTable
		? {
				"& .MuiDataGrid-columnHeaderCheckbox": {
					justifyContent: "center",
				},
				"& .MuiDataGrid-columnHeader": {
					backgroundColor: theme.palette.primary.light,
				},
				"& .MuiDataGrid-cellCheckbox .MuiCheckbox-root": {
					color: theme.palette.primary.light,
				},
				"& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
					color: theme.palette.primary.contrastText,
				},
				"& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent": {
					justifyContent: "center",
				},
				"& .MuiCheckbox-root .MuiSvgIcon-root": {
					borderColor: theme.palette.primary.light,
				},
			}
		: {};

	const tableStle = isPrimaryColor ? tableSx : secondaryTableStyles;

	return (
		<PaperCard
			elevation={elevation}
			header={
				<PaperCardHeader containerRef={containerRef} title={title} isLoading={isLoading} onReload={onReload} />
			}
			content={
				<Stack
					ref={containerRef}
					direction="column"
					minHeight={180}
					maxHeight={fixedHeight ? `${fixedHeight}px` : "100%"}
					width="100%"
					spacing={0}
					sx={{ overflowY: "hidden", position: "relative" }}
				>
					<DataGrid
						loading={isLoading}
						rows={rows}
						columns={columns}
						hideFooter
						isCellEditable={() => false}
						getRowHeight={() => (isSecondaryTable ? 48 : 60)}
						disableColumnMenu
						disableRowSelectionOnClick
						checkboxSelection={isSecondaryTable}
						rowSelectionModel={rowSelectionModel}
						onRowSelectionModelChange={onRowSelectionChange}
						onRowClick={onRowClick}
						disableColumnResize={isSecondaryTable}
						sx={{
							"& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
								border: 0,
							},
							width: "100%",
							minWidth: "max-content",
							overflowX: "auto",
							"& .MuiDataGrid-virtualScroller": {
								overflowX: "auto",
							},
							...tableStle,
						}}
						slots={{ noRowsOverlay: () => <CustomNoRowsOverlay /> }}
					/>

					{/* Show spinner while loading additional data */}
					{isSecondaryTable && isLoading && rows.length > 0 && (
						<Stack
							sx={{ background: ThemeColors.LightBackground }}
							direction="row"
							spacing={2}
							justifyContent="center"
							alignItems="center"
							py={1}
						>
							<CircularProgress size={18} sx={{ color: theme.palette.primary.light }} />
							<Typography variant="caption" color="text.primary">
								{t("greenrouterList/data loading")}
							</Typography>
						</Stack>
					)}

					{/* Show “all data loaded” message */}
					{isSecondaryTable && !hasMore && !isLoading && rows.length > 0 && (
						<Stack
							sx={{ background: ThemeColors.LightBackground }}
							direction="row"
							spacing={2}
							justifyContent="center"
							alignItems="center"
							py={1}
						>
							<Typography variant="caption" color="text.primary">
								{t("greenrouterList/all data has been loaded")}
							</Typography>
						</Stack>
					)}
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0, paddingBottom: 0 }}
		/>
	);
};

export default TableData;
